/**
 * EventOrganizersPanel — who else can run this meeting (§ G12).
 *
 * A meeting outlives one person's attention. The person chasing eight people
 * for their home airports on the Friday before is often not the person who
 * typed the event in, and until now authorization was a single string compare
 * against the creator, so there was no way to hand that over.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserPlus, ShieldCheck, Clock, X, Mail, MailX } from 'lucide-react';
import { travelGroupApi, type EventOrganizer, type InviteOrganizerResult } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import EmailPreviewDialog from '@/components/shared/EmailPreviewDialog';
import { organizerStatusNote } from './organizerRosterView';

export default function EventOrganizersPanel({ groupId }: { groupId: string }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  // § G37 — what the last invitation actually did, so "email sent" is a fact the
  // organizer can OPEN rather than a word they have to trust.
  const [lastInvite, setLastInvite] = useState<InviteOrganizerResult | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: organizers } = useQuery({
    queryKey: ['event-organizers', groupId],
    queryFn: () => travelGroupApi.listOrganizers(groupId).catch(() => null),
    retry: false,
  });

  const inviteMut = useMutation({
    mutationFn: () => travelGroupApi.inviteOrganizer(groupId, email.trim()),
    onSuccess: (r) => {
      setEmail('');
      setError('');
      // Say which of the two things happened, rather than a generic "invited".
      setNote(organizerStatusNote(r.email, r.status));
      setLastInvite(r);
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['event-organizers', groupId] });
    },
    onError: (e: any) => setError(e?.response?.data?.error || e.message),
  });

  const removeMut = useMutation({
    mutationFn: (id: string) => travelGroupApi.removeOrganizer(groupId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event-organizers', groupId] }),
    onError: (e: any) => setError(e?.response?.data?.error || e.message),
  });

  // A non-organizer gets a 403 and sees nothing.
  if (!organizers) return null;

  return (
    <section className="rounded-lg border border-border bg-background p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <ShieldCheck className="h-4 w-4" />
          Organizers
        </span>

        {organizers.map((o: EventOrganizer) => (
          <span
            key={o.id}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs"
          >
            {o.email || o.mcp_user_id}
            {o.is_owner && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">owner</span>}
            {o.status === 'pending' && (
              <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                <Clock className="h-3 w-3" /> pending
              </span>
            )}
            {!o.is_owner && (
              <button
                onClick={() => removeMut.mutate(o.id)}
                disabled={removeMut.isPending}
                className="text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${o.email}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}

        {!open && (
          <button
            onClick={() => {
              setOpen(true);
              setNote('');
              setError('');
              setLastInvite(null);
            }}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:bg-muted"
          >
            <UserPlus className="h-3 w-3" /> Add organizer
          </button>
        )}
      </div>

      {open && (
        <form
          className="mt-2 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) inviteMut.mutate();
          }}
        >
          <Input
            placeholder="their@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-7 w-56 text-xs"
            autoFocus
          />
          <Button type="submit" size="sm" className="h-7 text-xs" disabled={inviteMut.isPending || !email.trim()}>
            {inviteMut.isPending ? 'Inviting…' : 'Invite'}
          </Button>
          <button type="button" onClick={() => setOpen(false)} className="text-xs text-muted-foreground hover:text-foreground">
            cancel
          </button>
        </form>
      )}

      {note && (
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          {note}
          {/* § G37 — the affordance Wade asked for: "email sent" is a link that
              pops the actual message, formatted as the recipient received it. */}
          {lastInvite?.email_sent && lastInvite.email_log_id && (
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1 font-medium text-foreground underline underline-offset-2 hover:text-primary"
            >
              <Mail className="h-3 w-3" />
              email sent
            </button>
          )}
          {/* Sent but unloggable: the send happened, there is just nothing stored
              to show. Saying "email sent" without a link is honest; a dead link
              would not be. */}
          {lastInvite?.email_sent && !lastInvite.email_log_id && (
            <span className="inline-flex items-center gap-1">
              <Mail className="h-3 w-3" /> email sent
            </span>
          )}
          {lastInvite && !lastInvite.email_sent && (
            <span className="inline-flex items-center gap-1 text-destructive">
              <MailX className="h-3 w-3" /> we could not email them — send them the link yourself
            </span>
          )}
        </p>
      )}
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}

      <EmailPreviewDialog
        emailLogId={lastInvite?.email_log_id ?? null}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
    </section>
  );
}
